import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import api, { apiError } from '../lib/api';
import { formatIDR } from '../lib/format';
import { MoneyInput } from './MoneyInput';

/* Kartu "Denda" dan "Barang Rusak / Hilang" di halaman detail order.

   Jalur UTAMA tetap dialog Terima Pengembalian. Kartu-kartu ini jalur perbaikan
   kalau ada yang terlupa saat barang diterima — kejadian 25 Okt 2026: order
   2609-55 sudah diterima karyawan, customer sudah membayar dendanya, tapi
   dendanya lupa diisi.

   Hanya muncul untuk owner, dan hanya untuk order yang barangnya sudah kembali.
   Semua penjagaan angka ada di server; yang di sini supaya kesalahan ketahuan
   sebelum tombol ditekan. */

const JENIS = ['Keterlambatan', 'Kerusakan', 'Kehilangan'];
const SUMBER = [
  { v: 'cash', t: 'Tunai' },
  { v: 'bank_ops', t: 'Rek. Ops' },
  { v: 'bank_utama', t: 'Rek. Utama' },
];
const LABEL = { cash: 'Tunai', bank_ops: 'Rek. Operasional', bank_utama: 'Rek. Utama' };
const BULAN = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

function hariIni() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function selisihHari(dari, sampai) {
  if (!dari || !sampai) return 0;
  const a = Date.parse(`${dari}T00:00:00`);
  const b = Date.parse(`${sampai}T00:00:00`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86400000);
}

function tglId(iso) {
  if (!iso || iso.length < 10) return iso || '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${Number(d)} ${BULAN[Number(m) - 1]} ${y}`;
}

const angka = (v) => Number(String(v ?? '').replace(/[^0-9]/g, '') || 0);

const kelasChip = (aktif) =>
  `rounded-full px-3 py-1.5 text-[11px] font-semibold border ${
    aktif ? 'bg-brown text-white border-brown' : 'bg-white text-mocha border-brown/15'}`;

const kelasLabel = 'block text-[11px] uppercase tracking-wider text-mocha font-semibold mt-3 mb-1';
const kelasInput = 'w-full rounded-xl border border-input bg-ivory/50 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30';

function Kartu({ judul, testid, children }) {
  return (
    <div className="bg-white rounded-2xl border border-border shadow-soft p-5" data-testid={testid}>
      <h2 className="font-display text-lg text-brown-dark mb-3">{judul}</h2>
      {children}
    </div>
  );
}

/* ============================== DENDA ============================== */

export function KartuDenda({ tx, onDone }) {
  const tglKembali = (tx.returned_date || tx.ready_date || tx.return_date || '').slice(0, 10);
  const telat = Math.max(0, selisihHari(tx.return_date, tglKembali));
  const d = tx.denda && ['belum', 'lunas'].includes(tx.denda.status) ? tx.denda : null;

  const [mode, setMode] = useState('lihat');        // lihat | tambah | ubah | lunasi
  const [jenis, setJenis] = useState(['Keterlambatan']);
  const [nominal, setNominal] = useState('');
  const [sumber, setSumber] = useState('cash');
  const [tanggal, setTanggal] = useState(tglKembali);
  const [catatan, setCatatan] = useState('');
  const [konfirm, setKonfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Aksesoris bawaan yang ditandai hilang saat pengembalian tapi dendanya belum diisi:
  // formulir terbuka sudah berjenis Kehilangan dengan keterangannya terisi.
  const aksHilang = tx.pengembalian?.aksesoris_hilang || [];
  const ketAks = aksHilang.map((a) => `${a.nama} ${a.pcs} pcs hilang`).join(', ');

  const buka = (m) => {
    setKonfirm(false);
    if (m === 'tambah') {
      setJenis(aksHilang.length ? ['Kehilangan'] : telat > 0 ? ['Keterlambatan'] : []);
      setNominal('');
      setSumber('cash');
      setTanggal(tglKembali);          // kejadian tersering: dibayar saat barang kembali
      setCatatan(ketAks);
    } else if (m === 'ubah') {
      setJenis(d?.jenis || []);
      setNominal(String(Math.round(d?.nominal || 0)));
      setCatatan(d?.catatan || '');
    } else if (m === 'lunasi') {
      setSumber('cash');
      setTanggal(hariIni());
    }
    setMode(m);
  };

  const kerjakan = async (fn, pesan) => {
    setBusy(true);
    try {
      await fn();
      toast.success(pesan);
      setMode('lihat');
      setKonfirm(false);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const nilai = angka(nominal);
  const tglSah = tanggal && tanggal >= tglKembali && tanggal <= hariIni();

  const simpanBaru = () => kerjakan(
    () => api.post(`/transactions/${tx.id}/denda`, {
      jenis, nominal: nilai, sumber, catatan: catatan.trim(),
      tanggal: sumber === 'belum' ? null : tanggal,
    }),
    sumber === 'belum'
      ? `Denda ${formatIDR(nilai)} dicatat — belum dibayar`
      : `Denda ${formatIDR(nilai)} masuk kas, tanggal ${tglId(tanggal)}`);

  const simpanUbah = () => kerjakan(
    () => api.put(`/transactions/${tx.id}/denda`, { jenis, nominal: nilai, catatan: catatan.trim() }),
    'Denda diubah');

  const simpanLunas = () => kerjakan(
    () => api.post(`/transactions/${tx.id}/denda/bayar`, { sumber, tanggal }),
    `Denda ${formatIDR(d?.nominal)} masuk kas, tanggal ${tglId(tanggal)}`);

  const hapus = () => kerjakan(
    () => api.delete(`/transactions/${tx.id}/denda`),
    d?.status === 'lunas' ? 'Denda dibatalkan — baris kas di-void' : 'Denda dihapus');

  const pilihJenis = (j) => setJenis((s) => (s.includes(j) ? s.filter((x) => x !== j) : [...s, j]));

  const blokTanggal = (
    <div>
      <label className={kelasLabel} htmlFor="denda-tanggal">Tanggal uang diterima</label>
      <input
        id="denda-tanggal"
        data-testid="denda-tanggal"
        type="date"
        value={tanggal}
        min={tglKembali}
        max={hariIni()}
        onChange={(e) => setTanggal(e.target.value)}
        className={kelasInput}
      />
      <p className="text-[11px] text-mocha mt-1">
        {mode === 'lunasi'
          ? 'Terisi hari ini. Kalau uangnya sudah diterima lebih dulu, ganti ke tanggal itu.'
          : <>Terisi <b>tanggal barang kembali</b> ({tglId(tglKembali)}). Kalau uangnya baru diterima belakangan, ganti ke tanggal itu.</>}
      </p>
      {!tglSah && (
        <p className="text-[11px] text-danger font-bold mt-1">
          Tanggal harus antara {tglId(tglKembali)} dan hari ini.
        </p>
      )}
    </div>
  );

  const riwayat = tx.denda_riwayat || [];

  return (
    <Kartu judul="Denda" testid="kartu-denda">
      {/* ---------- belum ada denda ---------- */}
      {!d && mode === 'lihat' && (
        <>
          <p className="text-sm text-mocha">Belum ada denda tercatat untuk order ini.</p>
          <p className="text-[11px] text-mocha mt-0.5 mb-3">
            {telat > 0
              ? <>Barang kembali <b>{telat} hari</b> lewat jadwal ({tglId(tx.return_date)} → {tglId(tglKembali)}).</>
              : 'Barang kembali tepat waktu.'}
          </p>
          {aksHilang.length > 0 && (
            <p className="rounded-xl bg-orange-50 text-orange-800 text-[11px] font-semibold px-3 py-2 mb-3"
               data-testid="denda-aks-hint">
              Aksesoris hilang: {ketAks}. Denda ganti ruginya belum ditetapkan.
            </p>
          )}
          <button
            data-testid="denda-tambah"
            onClick={() => buka('tambah')}
            className="w-full rounded-xl bg-brown text-white py-2.5 text-sm font-bold hover:bg-brown-med"
          >
            + Tambah Denda
          </button>
        </>
      )}

      {/* ---------- isian tambah / ubah ---------- */}
      {(mode === 'tambah' || mode === 'ubah') && (
        <div>
          <label className={`${kelasLabel} mt-0`}>Jenis denda</label>
          <div className="flex flex-wrap gap-1.5">
            {JENIS.map((j) => (
              <button key={j} type="button" data-testid={`denda-jenis-${j}`}
                      onClick={() => pilihJenis(j)} className={kelasChip(jenis.includes(j))}>
                {j}
              </button>
            ))}
          </div>
          {telat > 0 && (
            <p className="text-[11px] text-mocha mt-1">
              Telat <b>{telat} hari</b> — dihitung sampai barang benar-benar kembali, bukan sampai hari ini.
            </p>
          )}

          <label className={kelasLabel} htmlFor="denda-nominal">Nominal</label>
          <MoneyInput
            id="denda-nominal"
            data-testid="denda-nominal"
            value={nominal}
            onChange={setNominal}
            placeholder="0"
            className={`${kelasInput} text-base font-bold`}
          />
          {nilai > 0 && <p className="text-[11px] text-mocha mt-1">terbaca: <b>{formatIDR(nilai)}</b></p>}

          {mode === 'tambah' && (
            <>
              <label className={kelasLabel}>Uangnya diterima lewat</label>
              <div className="flex flex-wrap gap-1.5">
                {[...SUMBER, { v: 'belum', t: 'Belum dibayar' }].map((s) => (
                  <button key={s.v} type="button" data-testid={`denda-sumber-${s.v}`}
                          onClick={() => setSumber(s.v)} className={kelasChip(sumber === s.v)}>
                    {s.t}
                  </button>
                ))}
              </div>
              {sumber === 'belum'
                ? (nilai > 0 && (
                  <p className="rounded-xl bg-orange-50 text-orange-800 text-[11px] font-semibold px-3 py-2 mt-2">
                    Tidak menyentuh saldo kas — masuk daftar "Denda belum dibayar" di menu Kas sampai dilunasi.
                  </p>))
                : blokTanggal}
            </>
          )}

          <label className={kelasLabel} htmlFor="denda-catatan">Keterangan</label>
          <input id="denda-catatan" data-testid="denda-catatan" type="text" value={catatan}
                 onChange={(e) => setCatatan(e.target.value)}
                 placeholder="boleh dikosongkan" className={kelasInput} />

          <div className="flex gap-2 mt-4">
            <button onClick={() => setMode('lihat')} disabled={busy}
                    className="rounded-xl border border-brown/15 text-mocha px-4 py-2.5 text-sm font-semibold">
              Batal
            </button>
            <button
              data-testid="denda-simpan"
              disabled={busy || nilai <= 0 || (mode === 'tambah' && sumber !== 'belum' && !tglSah)}
              onClick={mode === 'tambah' ? simpanBaru : simpanUbah}
              className="flex-1 rounded-xl bg-brown text-white py-2.5 text-sm font-bold hover:bg-brown-med disabled:opacity-40"
            >
              {busy ? 'Menyimpan…' : 'Simpan Denda'}
            </button>
          </div>
        </div>
      )}

      {/* ---------- melunasi denda yang belum dibayar ---------- */}
      {mode === 'lunasi' && d && (
        <div>
          <p className="text-sm">Lunasi denda <b>{formatIDR(d.nominal)}</b></p>
          <label className={kelasLabel}>Diterima lewat</label>
          <div className="flex flex-wrap gap-1.5">
            {SUMBER.map((s) => (
              <button key={s.v} type="button" data-testid={`denda-lunasi-${s.v}`}
                      onClick={() => setSumber(s.v)} className={kelasChip(sumber === s.v)}>
                {s.t}
              </button>
            ))}
          </div>
          {blokTanggal}
          <div className="flex gap-2 mt-4">
            <button onClick={() => setMode('lihat')} disabled={busy}
                    className="rounded-xl border border-brown/15 text-mocha px-4 py-2.5 text-sm font-semibold">
              Batal
            </button>
            <button data-testid="denda-lunasi-simpan" disabled={busy || !tglSah} onClick={simpanLunas}
                    className="flex-1 rounded-xl bg-success text-white py-2.5 text-sm font-bold disabled:opacity-40">
              {busy ? 'Menyimpan…' : 'Tandai Lunas'}
            </button>
          </div>
        </div>
      )}

      {/* ---------- denda sudah tercatat ---------- */}
      {d && mode === 'lihat' && (
        <div data-testid="denda-tercatat">
          <p className={`text-xs font-bold ${d.status === 'lunas' ? 'text-success' : 'text-orange-700'}`}>
            ● {d.status === 'lunas' ? 'Lunas' : 'Belum dibayar'}
            {d.susulan && <span className="ml-1.5 font-semibold text-mocha">· dicatat susulan</span>}
          </p>
          <p className="font-display text-2xl text-brown-dark mt-0.5">{formatIDR(d.nominal)}</p>
          <p className="text-xs text-mocha">
            {(d.jenis || []).join(' + ') || 'Denda'}
            {d.telat_hari > 0 && <> · telat {d.telat_hari} hari</>}
            {d.status === 'lunas' && d.sumber && <> · {LABEL[d.sumber] || d.sumber}</>}
            {d.status === 'lunas' && d.tanggal_terima && <> · diterima {tglId(d.tanggal_terima)}</>}
          </p>
          {d.catatan && <p className="text-xs text-mocha mt-1">{d.catatan}</p>}

          {!konfirm && d.status === 'belum' && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button data-testid="denda-lunasi" onClick={() => buka('lunasi')}
                      className="flex-1 min-w-[110px] rounded-xl bg-success text-white py-2.5 text-sm font-bold">
                Tandai Lunas
              </button>
              <button data-testid="denda-ubah" onClick={() => buka('ubah')}
                      className="flex-1 min-w-[80px] rounded-xl border border-brown/15 text-brown-dark py-2.5 text-sm font-semibold">
                Ubah
              </button>
              <button data-testid="denda-hapus" onClick={() => setKonfirm(true)}
                      className="flex-1 min-w-[80px] rounded-xl border border-danger/30 text-danger py-2.5 text-sm font-semibold">
                Hapus
              </button>
            </div>
          )}
          {!konfirm && d.status === 'lunas' && (
            <>
              <button data-testid="denda-batalkan" onClick={() => setKonfirm(true)}
                      className="w-full mt-3 rounded-xl border border-danger/30 text-danger py-2.5 text-sm font-semibold">
                Batalkan Denda
              </button>
              <p className="text-[11px] text-mocha mt-1.5">
                Denda yang sudah lunas tidak bisa diubah langsung. Kalau salah angka, batalkan lalu
                catat ulang — supaya uang yang sudah tercatat tidak pernah berubah diam-diam.
              </p>
            </>
          )}

          {konfirm && (
            <div className="rounded-xl bg-orange-50 text-orange-900 text-xs px-3 py-2.5 mt-3"
                 data-testid="denda-konfirm">
              <b>{d.status === 'lunas' ? 'Batalkan' : 'Hapus'} denda {formatIDR(d.nominal)}?</b>
              {d.status === 'lunas' && (
                <span className="block mt-0.5">Baris kasnya di-void — tetap terlihat sebagai Void, tidak dihapus.</span>
              )}
              <div className="flex gap-2 mt-2">
                <button onClick={() => setKonfirm(false)} disabled={busy}
                        className="flex-1 rounded-lg border border-brown/15 bg-white py-2 font-semibold">
                  Tidak jadi
                </button>
                <button data-testid="denda-konfirm-ya" onClick={hapus} disabled={busy}
                        className="flex-1 rounded-lg bg-danger text-white py-2 font-bold disabled:opacity-40">
                  {busy ? '…' : d.status === 'lunas' ? 'Ya, batalkan' : 'Ya, hapus'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {riwayat.length > 0 && mode === 'lihat' && (
        <div className="border-t border-dashed border-brown/15 mt-3 pt-2 space-y-0.5" data-testid="denda-riwayat">
          {riwayat.slice(-5).map((r, i) => (
            <p key={i} className="text-[11px] text-mocha">· {r.aksi} — {tglId((r.at || '').slice(0, 10))}</p>
          ))}
        </div>
      )}
    </Kartu>
  );
}

/* ======================== BARANG RUSAK / HILANG ======================== */

export function KartuBarangSusulan({ tx, onDone }) {
  const [buka, setBuka] = useState(false);
  const [jml, setJml] = useState({});
  const [catatan, setCatatan] = useState('');
  const [busy, setBusy] = useState(false);
  const [peringatan, setPeringatan] = useState([]);

  // Satu baris per (barang, ukuran), termasuk bawahan — sama dengan dialog
  // Terima Pengembalian, supaya tidak ada dua cara yang perlu dipelajari.
  const potongan = useMemo(() => {
    const peta = new Map();
    const tambah = (pid, kode, nama, size, qty) => {
      const q = Number(qty) || 0;
      if (!pid || q <= 0) return;
      const key = `${pid}|${size}`;
      const ada = peta.get(key);
      if (ada) ada.qty += q;
      else peta.set(key, { key, product_id: pid, code: kode || '', name: nama || '', size, qty: q });
    };
    (tx.rows || []).forEach((r) => {
      Object.entries(r.sizes || {}).forEach(([s, q]) => tambah(r.product_id, r.product_code, r.product_name, s, q));
      Object.entries(r.bawahan_sizes || {}).forEach(([s, q]) => tambah(r.bawahan_product_id, r.bawahan_code, r.bawahan_name, s, q));
    });
    return [...peta.values()];
  }, [tx.rows]);

  const pg = tx.pengembalian || {};
  const tercatat = [
    ...(pg.rusak || []).map((x) => ({ ...x, jenis: 'rusak' })),
    ...(pg.hilang || []).map((x) => ({ ...x, jenis: 'hilang' })),
  ];
  const sudah = {};
  tercatat.forEach((x) => {
    const k = `${x.product_id}|${x.size}`;
    sudah[k] = (sudah[k] || 0) + (Number(x.pcs) || 0);
  });

  const ambil = (key, j) => angka(jml[`${key}|${j}`]);
  const lebih = potongan.filter((p) => (sudah[p.key] || 0) + ambil(p.key, 'r') + ambil(p.key, 'h') > p.qty);
  const adaIsi = potongan.some((p) => ambil(p.key, 'r') + ambil(p.key, 'h') > 0);

  const simpan = async () => {
    const rusak = [];
    const hilang = [];
    potongan.forEach((p) => {
      if (ambil(p.key, 'r') > 0) rusak.push({ product_id: p.product_id, size: p.size, pcs: ambil(p.key, 'r') });
      if (ambil(p.key, 'h') > 0) hilang.push({ product_id: p.product_id, size: p.size, pcs: ambil(p.key, 'h') });
    });
    setBusy(true);
    try {
      const res = await api.post(`/transactions/${tx.id}/barang-susulan`, {
        rusak, hilang, catatan: catatan.trim(),
      });
      const pk = res?.data?.peringatan_stok || [];
      setPeringatan(pk);
      toast.success('Barang rusak/hilang tercatat');
      if (pk.length) toast.warning('Ada order mendatang yang jadi kekurangan barang — lihat kartu Barang Rusak / Hilang', { duration: 10000 });
      setBuka(false);
      setJml({});
      setCatatan('');
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Kartu judul="Barang Rusak / Hilang" testid="kartu-barang-susulan">
      {tercatat.length === 0 && !buka && (
        <p className="text-sm text-mocha">Saat diterima kembali: <b>semua lengkap &amp; utuh</b>.</p>
      )}

      {tercatat.length > 0 && (
        <div className="space-y-1.5" data-testid="barang-tercatat">
          {tercatat.map((x, i) => (
            <div key={i} className="flex items-start justify-between gap-2 rounded-xl bg-ivory/70 px-3 py-2 text-xs">
              <span>
                <b className={x.jenis === 'hilang' ? 'text-danger' : ''}>
                  {x.pcs} pcs {x.jenis === 'hilang' ? 'HILANG' : 'rusak'}
                </b>{' '}
                {x.code} {x.name} · ukuran {x.size}
                {x.catatan && <span className="block text-mocha">{x.catatan}</span>}
              </span>
              {x.susulan && <span className="text-[10px] text-mocha whitespace-nowrap">susulan</span>}
            </div>
          ))}
        </div>
      )}

      {peringatan.length > 0 && (
        <div className="rounded-xl bg-orange-50 text-orange-900 text-xs px-3 py-2.5 mt-3"
             data-testid="barang-peringatan">
          <b>⚠️ Barang ini sudah dipesan lagi.</b>
          {peringatan.map((p, i) => <span key={i} className="block mt-0.5">{p}</span>)}
          <span className="block mt-1 text-[11px]">
            Pencatatannya tetap disimpan — barang yang hilang memang hilang. Order di atas perlu
            dicarikan pengganti atau customernya dihubungi.
          </span>
        </div>
      )}

      {!buka && (
        <button data-testid="barang-buka" onClick={() => setBuka(true)}
                className="w-full mt-3 rounded-xl border border-brown/15 text-brown-dark py-2.5 text-sm font-semibold">
          {tercatat.length ? '+ Tandai lagi' : '+ Tandai rusak / hilang yang terlupa'}
        </button>
      )}

      {buka && (
        <div className="mt-2">
          <p className="text-[11px] text-mocha mb-1">
            Isi jumlah per ukuran. Yang sudah pernah ditandai ikut dihitung — totalnya tidak bisa melebihi yang disewa.
          </p>
          {potongan.map((p) => (
            <div key={p.key} className="flex items-center gap-2 py-2 border-b border-brown/5 last:border-0">
              <span className="flex-1 min-w-0 text-xs">
                <b>{p.code}</b> {p.name}
                <span className="block text-[11px] text-mocha">
                  ukuran {p.size} · disewa {p.qty}
                  {sudah[p.key] ? ` · sudah ditandai ${sudah[p.key]}` : ''}
                </span>
              </span>
              {[['r', 'Rusak', 'border-orange-300'], ['h', 'Hilang', 'border-red-300']].map(([j, t, w]) => (
                <label key={j} className="text-[10px] text-mocha text-center">
                  {t}
                  <input
                    data-testid={`barang-${j}-${p.product_id}-${p.size}`}
                    type="text"
                    inputMode="numeric"
                    value={jml[`${p.key}|${j}`] ?? ''}
                    placeholder="0"
                    onChange={(e) => setJml((s) => ({ ...s, [`${p.key}|${j}`]: e.target.value.replace(/[^0-9]/g, '') }))}
                    className={`block w-14 rounded-lg border bg-ivory/50 px-2 py-1.5 text-sm text-center outline-none focus:ring-2 focus:ring-brown/30 ${w}`}
                  />
                </label>
              ))}
            </div>
          ))}

          {lebih.length > 0 && (
            <p className="rounded-xl bg-danger/10 text-danger text-[11px] font-bold px-3 py-2 mt-2"
               data-testid="barang-lebih">
              Melebihi yang disewa: {lebih.map((x) => `${x.code} ${x.size}`).join(', ')}. Betulkan dulu sebelum disimpan.
            </p>
          )}

          <label className={kelasLabel} htmlFor="barang-catatan">Keterangan</label>
          <input id="barang-catatan" data-testid="barang-catatan" type="text" value={catatan}
                 onChange={(e) => setCatatan(e.target.value)}
                 placeholder='mis. "noda tinta, baru ketahuan saat dilaundry"' className={kelasInput} />

          <div className="flex gap-2 mt-4">
            <button onClick={() => { setBuka(false); setJml({}); }} disabled={busy}
                    className="rounded-xl border border-brown/15 text-mocha px-4 py-2.5 text-sm font-semibold">
              Batal
            </button>
            <button data-testid="barang-simpan" onClick={simpan}
                    disabled={busy || !adaIsi || lebih.length > 0}
                    className="flex-1 rounded-xl bg-brown text-white py-2.5 text-sm font-bold hover:bg-brown-med disabled:opacity-40">
              {busy ? 'Menyimpan…' : 'Simpan'}
            </button>
          </div>
          <p className="text-[11px] text-mocha mt-2">
            Salah tandai? <b>Rusak</b> dipulihkan dari menu Stok seperti biasa. <b>Hilang</b> dikoreksi
            dengan mengubah jumlah stok di menu Stok.
          </p>
        </div>
      )}
    </Kartu>
  );
}
